import { useEffect } from "react";

import { useParams } from "@tanstack/react-router";
import { useDispatch } from "react-redux";

import { useAssessmentByCaseNumber } from "@hooks/queries/use-assessments";
import { useCaseByCaseNumber } from "@hooks/queries/use-cases";

import { showError } from "@utils/notification";
import { cn } from "@utils/ui";

import {
  ASSESSMENT_SECTIONS,
  ASSESSMENT_STATUS,
  AssessmentSection,
  SECTIONS_BY_SERVICE_TYPE,
} from "../constants/sections";
import { useAutoExport } from "../hooks/useAutoExport";

type TabLinksProps = {
  selectedId: string;
  handleFeatureItemClick: (id: string) => void;
};
export const TabLinks = ({ selectedId, handleFeatureItemClick }: TabLinksProps) => {
  const dispatch = useDispatch();
  const { id: caseNumber } = useParams({ strict: false }) as { id: string };

  const {
    data: caseData,
    isLoading: isCaseLoading,
    error: caseError,
  } = useCaseByCaseNumber({
    case_number: caseNumber || "",
    enabled: !!caseNumber,
  });

  const { data: assessmentData, isLoading: isAssessmentLoading } =
    useAssessmentByCaseNumber({
      caseNumber: caseNumber || "",
      enabled: !!caseNumber,
    });

  // Auto-export functionality for assessment fields
  useAutoExport({
    assessmentData,
    assessmentId: assessmentData?.id,
    caseNumber: caseNumber || "",
    enabled: !!assessmentData && !!caseNumber,
  });

  useEffect(() => {
    if (!caseNumber) {
      showError("No case selected", dispatch);
      return;
    }
  }, [caseNumber, dispatch]);

  useEffect(() => {
    if (caseError) {
      showError("Error fetching case details", dispatch);
    }
  }, [caseError, dispatch]);

  const getSections = (): AssessmentSection[] => {
    // resolveSections mirrors the original precedence: a valid service-type list
    // wins; otherwise fall back to the legacy template-order sections; otherwise
    // empty. Kept as a nested helper so the notes append below runs on all paths.
    const resolveSections = (): AssessmentSection[] => {
      // Service-type-based sections take priority over legacy template-based ones.
      if (caseData?.service_type) {
        const ids = SECTIONS_BY_SERVICE_TYPE[caseData.service_type];
        if (ids?.length) {
          return ids
            .map((id) => ASSESSMENT_SECTIONS.find((s) => s.id === id))
            .filter((s): s is AssessmentSection => Boolean(s));
        }
      }
      // Fallback: legacy template-order-based sections.
      if (caseData?.template?.order) {
        return ASSESSMENT_SECTIONS.filter((s) =>
          s.templateIndex?.includes(caseData.template!.order)
        );
      }
      return [];
    };

    const sections = resolveSections();

    // MDRS-136: `pinLast` sections (Assessment Notes) always render last, once —
    // drop any the resolved list already contributed, then append them.
    const pinned = ASSESSMENT_SECTIONS.filter((s) => s.pinLast);
    const pinnedIds = new Set(pinned.map((s) => s.id));
    const rest = sections.filter((s) => !pinnedIds.has(s.id));
    return [...rest, ...pinned];
  };

  const renderTabs = () => {
    if (isCaseLoading || isAssessmentLoading) {
      return <LoadingSkeleton />;
    }

    const sections = getSections();

    if (sections.length === 0) {
      return (
        <div className="text-foreground-muted">
          No sections available for this case
        </div>
      );
    }

    return (
      <>
        {sections.map((item) => {
          let isCompleted = false;
          let isExported = false;

          if (assessmentData && item.key) {
            const field = assessmentData[item.key as keyof typeof assessmentData];
            // Assessment Notes (key "notes") is a plain string, not a
            // {content, status} field — it has no status to accept/export. Tick
            // it off once notes have been entered, mirroring how summarised
            // sections show completion (MDRS-161).
            if (item.key === "notes") {
              isCompleted = typeof field === "string" && field.trim().length > 0;
            } else if (field && typeof field === "object" && "status" in field) {
              // Check if the field has status information
              isExported =
                field.status === ASSESSMENT_STATUS.ACCEPTED ||
                field.status === ASSESSMENT_STATUS.EXPORTED;
            }
          }

          return (
            <TabItem
              key={item.id}
              id={item.id}
              title={item.title}
              isActive={selectedId === item.id}
              onClick={handleFeatureItemClick}
              isCompleted={isCompleted}
              isExported={isExported}
            />
          );
        })}
      </>
    );
  };

  return (
    <div className="flex h-full w-full flex-col overflow-y-auto rounded-[10px] border border-border bg-background-white">
      {renderTabs()}
    </div>
  );
};

/** Visual state of a section row's leading status indicator (MDRS-114). */
type SectionStatus = "active" | "completed" | "incomplete";

type TabItemProps = {
  id: string;
  title: string;
  isActive: boolean;
  isCompleted?: boolean;
  isExported?: boolean;
  onClick: (id: string) => void;
};

const TabItem = ({
  id,
  title,
  isActive,
  isCompleted,
  isExported,
  onClick,
}: TabItemProps) => {
  // The active row always wins the indicator slot. Otherwise an exported /
  // completed section shows the green check; everything else is incomplete.
  // (Phase 1 maps the redesign's three visual states onto the existing
  // exported/active data — no new completion semantics, MDRS-114.)
  const status: SectionStatus = isActive
    ? "active"
    : isCompleted || isExported
      ? "completed"
      : "incomplete";

  return (
    <button
      key={id}
      type="button"
      data-section-id={id}
      data-status={status}
      aria-current={isActive ? "true" : undefined}
      className={cn(
        "flex w-full items-center gap-2.5 border-b border-border/60 px-4 py-[11px] text-left transition-colors",
        "text-xs text-foreground",
        "cursor-pointer hover:bg-primary-50/60",
        status === "active" &&
          "pointer-events-none bg-primary-50 font-semibold text-primary"
      )}
      onClick={() => onClick(id)}
    >
      <SectionStatusIcon status={status} />
      <span className="min-w-0 flex-1 truncate">{title}</span>
    </button>
  );
};

const SectionStatusIcon = ({ status }: { status: SectionStatus }) => {
  if (status === "completed") {
    return (
      <span
        aria-hidden="false"
        className="flex size-4 shrink-0 items-center justify-center rounded-full bg-success-600 text-[8px] font-bold text-white"
      >
        ✓
      </span>
    );
  }

  if (status === "active") {
    return (
      <span className="size-4 shrink-0 rounded-full bg-primary" aria-hidden="true" />
    );
  }

  return (
    <span
      className="size-4 shrink-0 rounded-full border-[1.5px] border-border bg-background-white"
      aria-hidden="true"
    />
  );
};

const LoadingSkeleton = () => (
  <>
    {Array(5)
      .fill(0)
      .map((_, index) => (
        <div
          key={index}
          className="mx-4 my-1.5 block h-8 animate-pulse rounded-md bg-dark-100/40 dark:bg-dark-50/30"
        />
      ))}
  </>
);
